import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, Plus, Trash2, UserCheck, UserX, Clock, Mail, Download, Send, ArrowRight } from '../icons';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { onRsvpUpdate } from '../services/socket';
import { toast } from 'react-toastify';
import { useConfirm } from '../components/Modal';
import { getWeddings, getGuests, addGuest, deleteGuest } from '../services/api';

function GuestList() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [confirm, ConfirmDialog] = useConfirm();
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState({});
  const [weddingId, setWeddingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filterGroup, setFilterGroup] = useState('all');
  const [filterRsvp, setFilterRsvp] = useState('all');
  const [newGuest, setNewGuest] = useState({
    first_name: '', last_name: '', email: '', phone: '', group_name: 'General', meal_preference: 'Non-vegetarian', plus_one: false
  });

  const loadGuestData = useCallback(async (wId) => {
    try {
      const res = await getGuests(wId);
      if (res) {
        setGuests(res.data.guests);
        setStats(res.data.stats);
      }
    } catch {
      // toastId keeps repeated realtime reloads from stacking the same error
      toast.error(t('guestList.loadFailed'), { toastId: 'guest-load-failed' });
    }
  }, [t]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    // Only couples and delegates can access guest list
    if (user.role === 'helper' && user.collaborator?.collaborator_role !== 'delegate') {
      navigate('/checklist');
      return;
    }
    getWeddings()
      .then((res) => {
        if (res.data.length > 0) {
          setWeddingId(res.data[0].id);
          return loadGuestData(res.data[0].id);
        }
      })
      .catch(() => toast.error(t('guestList.loadFailed'), { toastId: 'guest-load-failed' }))
      .finally(() => setLoading(false));
  }, [user, navigate, authLoading, loadGuestData, t]);

  // Real-time RSVP updates
  useEffect(() => {
    if (!weddingId) return;
    const unsub = onRsvpUpdate(() => { loadGuestData(weddingId); });
    return unsub;
  }, [weddingId, loadGuestData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await addGuest(weddingId, newGuest);
      setNewGuest({ first_name: '', last_name: '', email: '', phone: '', group_name: 'General', meal_preference: 'Non-vegetarian', plus_one: false });
      setShowAdd(false);
      // Adding a guest never emails them; say so, since couples expect it to
      toast.success(t('guestList.guestAdded'));
      // Reload rather than append so the RSVP counts above stay in step
      loadGuestData(weddingId);
    } catch (err) {
      toast.error(err.response?.data?.error || t('guestList.addFailed'));
    }
  };

  const handleDelete = async (guest) => {
    // The server also deletes this guest's linked invitation (guests.js DELETE)
    const name = `${guest.first_name} ${guest.last_name || ''}`.trim();
    const ok = await confirm({
      title: t('guestList.deleteGuest'),
      message: t(guest.invitation_sent ? 'guestList.deleteConfirmInvited' : 'guestList.deleteConfirm').replace('{name}', name),
      danger: true,
      confirmText: t('common.delete'),
    });
    if (!ok) return;
    try {
      await deleteGuest(guest.id);
      loadGuestData(weddingId);
    } catch (err) {
      toast.error(err.response?.data?.error || t('guestList.deleteFailed'));
    }
  };

  // 'pending' hides two different situations: the couple hasn't sent an
  // invitation yet (something for them to do), or it went out and the guest
  // hasn't answered. The server flags which via invitation_sent.
  const rsvpKey = (g) => (g.rsvp_status === 'pending' ? (g.invitation_sent ? 'awaiting' : 'not_sent') : g.rsvp_status);
  const rsvpColors = { accepted: '#22C55E', declined: '#EF4444', awaiting: '#F59E0B', not_sent: '#DC2626', maybe: '#3B82F6' };
  const rsvpLabels = {
    accepted: t('guestList.accepted'),
    declined: t('guestList.declined'),
    awaiting: t('guestList.awaitingReply'),
    not_sent: t('guestList.rsvpNotSent'),
    maybe: t('guestList.maybe'),
  };

  const groups = [...new Set(guests.map(g => g.group_name))].filter(Boolean);
  const filteredGuests = guests.filter(g => {
    if (filterGroup !== 'all' && g.group_name !== filterGroup) return false;
    if (filterRsvp !== 'all' && rsvpKey(g) !== filterRsvp) return false;
    return true;
  });

  if (loading) return <div className="loading-page"><div className="spinner"></div></div>;

  return (
    <div className="guest-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1><Users size={28} /> {t('guestList.title')}</h1>
            <p>{stats.total || 0} {t('guestList.totalGuests')}</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-outline" onClick={() => {
              const data = guests.map(g => ({
                'First Name': g.first_name,
                'Last Name': g.last_name,
                Email: g.email || '',
                Phone: g.phone || '',
                Group: g.group_name || '',
                'RSVP Status': rsvpLabels[rsvpKey(g)] || g.rsvp_status,
                'Meal Preference': g.meal_preference || '',
                'Plus One': g.plus_one ? 'Yes' : 'No'
              }));
              const ws = XLSX.utils.json_to_sheet(data);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'Guest List');
              XLSX.writeFile(wb, 'wedding-guest-list.xlsx');
            }} disabled={guests.length === 0}>
              <Download size={16} /> {t('guestList.exportExcel')}
            </button>
            <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}>
              <Plus size={16} /> {t('guestList.addGuest')}
            </button>
          </div>
        </div>

        {/* RSVP Stats */}
        <div className="rsvp-stats">
          <div className="rsvp-stat" style={{ borderLeftColor: '#22C55E' }}>
            <UserCheck size={20} color="#22C55E" />
            <div>
              <span className="rsvp-count">{stats.accepted || 0}</span>
              <span className="rsvp-label">{t('guestList.accepted')}</span>
            </div>
          </div>
          <div className="rsvp-stat" style={{ borderLeftColor: '#EF4444' }}>
            <UserX size={20} color="#EF4444" />
            <div>
              <span className="rsvp-count">{stats.declined || 0}</span>
              <span className="rsvp-label">{t('guestList.declined')}</span>
            </div>
          </div>
          <div className="rsvp-stat" style={{ borderLeftColor: '#F59E0B' }}>
            <Clock size={20} color="#F59E0B" />
            <div>
              <span className="rsvp-count">{stats.awaiting || 0}</span>
              <span className="rsvp-label">{t('guestList.awaitingReply')}</span>
            </div>
          </div>
          <div className="rsvp-stat" style={{ borderLeftColor: '#DC2626' }}>
            <Send size={20} color="#DC2626" />
            <div>
              <span className="rsvp-count">{stats.not_sent || 0}</span>
              <span className="rsvp-label">{t('guestList.rsvpNotSent')}</span>
              {Number(stats.not_sent) > 0 && (
                <Link to="/invitations" className="rsvp-stat-link">
                  {t('guestList.sendInvitations')} <ArrowRight size={12} />
                </Link>
              )}
            </div>
          </div>
          <div className="rsvp-stat" style={{ borderLeftColor: '#3B82F6' }}>
            <Mail size={20} color="#3B82F6" />
            <div>
              <span className="rsvp-count">{stats.maybe || 0}</span>
              <span className="rsvp-label">{t('guestList.maybe')}</span>
            </div>
          </div>
        </div>

        {/* Add Guest Form */}
        {showAdd && (
          <form className="add-form guest-form" onSubmit={handleAdd}>
            <div className="form-row">
              <input type="text" placeholder={t('guestList.firstName')} value={newGuest.first_name} onChange={(e) => setNewGuest({ ...newGuest, first_name: e.target.value })} required />
              <input type="text" placeholder={t('guestList.lastName')} value={newGuest.last_name} onChange={(e) => setNewGuest({ ...newGuest, last_name: e.target.value })} />
            </div>
            <div className="form-row">
              <input type="email" placeholder={t('guestList.email')} value={newGuest.email} onChange={(e) => setNewGuest({ ...newGuest, email: e.target.value })} />
              <input type="tel" placeholder={t('guestList.phone')} value={newGuest.phone} onChange={(e) => setNewGuest({ ...newGuest, phone: e.target.value })} />
            </div>
            <div className="form-row">
              <select value={newGuest.group_name} onChange={(e) => setNewGuest({ ...newGuest, group_name: e.target.value })}>
                <option value="General">{t('guestList.general')}</option>
                <option value="Family - Groom">{t('guestList.familyGroom')}</option>
                <option value="Family - Bride">{t('guestList.familyBride')}</option>
                <option value="Friends - Groom">{t('guestList.friendsGroom')}</option>
                <option value="Friends - Bride">{t('guestList.friendsBride')}</option>
                <option value="College Friends">{t('guestList.collegeFriends')}</option>
                <option value="Work Colleagues">{t('guestList.workColleagues')}</option>
                <option value="Neighbours">{t('guestList.neighbours')}</option>
              </select>
              <select value={newGuest.meal_preference} onChange={(e) => setNewGuest({ ...newGuest, meal_preference: e.target.value })}>
                <option value="Non-vegetarian">{t('guestList.nonVeg')}</option>
                <option value="Vegetarian">{t('guestList.vegetarian')}</option>
                <option value="Vegan">{t('guestList.vegan')}</option>
              </select>
            </div>
            <label className="checkbox-inline">
              <input type="checkbox" checked={newGuest.plus_one} onChange={(e) => setNewGuest({ ...newGuest, plus_one: e.target.checked })} />
              {t('guestList.plusOne')}
            </label>
            <p className="guest-form-hint">
              {t('guestList.addHint')} <Link to="/invitations">{t('guestList.addHintLink')}</Link>. {t('guestList.addHintEmail')}
            </p>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">{t('guestList.addGuest')}</button>
              <button type="button" className="btn btn-outline" onClick={() => setShowAdd(false)}>{t('common.cancel')}</button>
            </div>
          </form>
        )}

        {/* Filters */}
        <div className="guest-filters">
          <select value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
            <option value="all">{t('guestList.allGroups')}</option>
            {groups.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={filterRsvp} onChange={(e) => setFilterRsvp(e.target.value)}>
            <option value="all">{t('guestList.allRsvp')}</option>
            <option value="accepted">{t('guestList.accepted')}</option>
            <option value="declined">{t('guestList.declined')}</option>
            <option value="awaiting">{t('guestList.awaitingReply')}</option>
            <option value="not_sent">{t('guestList.rsvpNotSent')}</option>
            <option value="maybe">{t('guestList.maybe')}</option>
          </select>
        </div>

        {/* Guest Table */}
        <div className="guest-table-wrap">
          <table className="guest-table">
            <thead>
              <tr>
                <th>{t('guestList.name')}</th>
                <th>{t('guestList.group')}</th>
                <th>{t('guestList.contact')}</th>
                <th>{t('guestList.rsvp')}</th>
                <th>{t('guestList.meal')}</th>
                <th>+1</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredGuests.map((guest) => (
                <tr key={guest.id}>
                  <td><strong>{guest.first_name} {guest.last_name}</strong></td>
                  <td><span className="group-badge">{guest.group_name}</span></td>
                  <td className="contact-cell">
                    {guest.email && <span>{guest.email}</span>}
                    {guest.phone && <span>{guest.phone}</span>}
                    {/* Explains why "RSVP not sent" can't be fixed from the Invitations page */}
                    {!guest.email && <span className="contact-no-email">{t('guestList.noEmail')}</span>}
                  </td>
                  <td>
                    <span
                      className={`rsvp-badge${rsvpKey(guest) === 'not_sent' ? ' rsvp-badge--not-sent' : ''}`}
                      style={{ background: rsvpColors[rsvpKey(guest)] + '20', color: rsvpColors[rsvpKey(guest)] }}
                    >
                      {rsvpLabels[rsvpKey(guest)] || guest.rsvp_status}
                    </span>
                  </td>
                  <td>{guest.meal_preference}</td>
                  <td>{guest.plus_one ? '✓' : '—'}</td>
                  <td>
                    <button className="delete-btn" title={t('guestList.deleteGuest')} onClick={() => handleDelete(guest)}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {ConfirmDialog}
    </div>
  );
}

export default GuestList;
